import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Renders its children once the box has been near the viewport, and keeps
 * them from then on.
 *
 * The Sammelprüfung draws every loaded row at once — that is the point of the
 * screen, a column you read straight down — and each row's crop is an
 * authenticated blob request of its own. A page of 25 scans with a dozen
 * flagged fields each therefore opened ~300 simultaneous fetches on first
 * paint, which fills the browser's per-host connection pool and puts the
 * reviewer's first save and the next page of documents in the queue behind
 * them. Deferring the crops below the fold costs nothing: nobody is looking
 * at them yet.
 *
 * Once shown, always shown: a row that unloaded its crop on scroll would
 * re-fetch it on the way back, and a reviewer scrolling up to re-check a
 * value they just corrected is the most likely person to do that.
 *
 * Without an `IntersectionObserver` (jsdom, an old browser) everything
 * renders at once — the behaviour before this existed, which is slow rather
 * than broken.
 */
export function NearViewport({
  children,
  placeholder,
  className,
  /** How far ahead of the fold to start, so a crop is loaded by the time it
   *  is scrolled to rather than after. */
  margin = '600px',
}: {
  children: ReactNode
  placeholder?: ReactNode
  /** The wrapper's own classes. It must have a box of its own — a
   *  `display: contents` wrapper is invisible to the observer and would
   *  never load anything. */
  className?: string
  margin?: string
}) {
  const host = useRef<HTMLDivElement>(null)
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined')

  useEffect(() => {
    if (near) return
    const node = host.current
    if (!node) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setNear(true)
      },
      { rootMargin: margin },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [near, margin])

  return (
    <div ref={host} className={className}>
      {near ? children : placeholder}
    </div>
  )
}
